<template>
  <div class="follow-up-rules-container">
    <span class="rules-message">Attendant can follow up with leads who match the following rules</span>
    <div class="rules-list-container">
      <div class="rules">
        <div v-for="(rule, index) in providedRules" :key="index">
          <Rule
            class="rule"
            :rule="rule"
            :rule-conditions-manager="ruleConditionsManager"
            @updated="
              (rule: RuleViewModel, hasInvalidConditions: boolean) => ruleUpdated(index, rule, hasInvalidConditions)
            "
            @deleted="deleteRule(index)"
          ></Rule>
        </div>

        <div class="add-label">
          <a @click="addRule">
            <AddIcon></AddIcon>
            <span class="add-rule-label">Add rule</span>
          </a>
        </div>
      </div>
    </div>

    <div class="follow-up-rule">
      <TagBox :value="rules.length > 0 ? 'else' : 'always'" />

      <CapableAppPicker
        :modalHeading="'Bot Apps'"
        :capability="'botActions'"
        :selected-apps="selectedApps"
        :isLozengeMode="true"
        :configRequest="configRequest"
        @change="selectedFollowUpActionsChanged"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import Rule from './Rule.vue';
import AddIcon from 'vue-material-design-icons/Plus.vue';
import { RuleViewModel } from '../../models/ruleViewModel';
import { ConfiguredAppIntegration } from '@/features/apps/shared/models/configuredAppIntegration';
import TagBox from '@/shared/components/TagBox.vue';
import CapableAppPicker from '@/shared/components/capableAppPicker/CapableAppPicker.vue';
import { ConfigRequest } from '@/shared/components/capableAppPicker/configRequest';
import { BotActionAppViewModel } from '@/features/bots/models';
import { PropType, ref, watch } from 'vue';
import { IConditionsManager } from '@shared/components/conditions/models/conditionsManager';
import useVuelidate from '@vuelidate/core';

const props = defineProps({
  configRequest: {
    type: Function as PropType<() => Promise<ConfigRequest>>,
    required: true,
  },
  rules: {
    type: Array as PropType<RuleViewModel[]>,
    default: [],
  },
  selectedApps: {
    type: Array as PropType<Array<ConfiguredAppIntegration>>,
    default: [],
  },
  ruleConditionsManager: {
    type: Object as PropType<IConditionsManager>,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'rules-updated', rules: RuleViewModel[]): void;
  (e: 'followup-action-change', actions: BotActionAppViewModel[]): void;
}>();

defineExpose({
  areRulesAreValid,
});

const providedRules = ref<RuleViewModel[]>([]);
const ruleInvalidStates = ref<boolean[]>([]);
const hasInvalidRuleConditions = ref(false);

watch(
  () => props.rules,
  (value: RuleViewModel[]) => {
    if (value && value.length > 0) {
      providedRules.value = value;
    }
  },
  { immediate: true, deep: true },
);

function addRule() {
  const rule = {
    conditions: {
      type: 'and',
      conditions: [],
    },
    actions: [],
  };
  providedRules.value.push(rule);
  ruleUpdated(providedRules.value.length - 1, rule, hasAnyInvalidRules());
}

function ruleUpdated(index: number, rule: RuleViewModel, hasInvalidConditions: boolean) {
  providedRules.value[index] = rule;
  ruleInvalidStates.value[index] = hasInvalidConditions;
  hasInvalidRuleConditions.value = hasAnyInvalidRules();

  emit('rules-updated', providedRules.value);
}

function selectedFollowUpActionsChanged(selectedApps: BotActionAppViewModel[]) {
  emit('followup-action-change', selectedApps);
}

function deleteRule(index: number) {
  providedRules.value.splice(index, 1);
}

const $v = useVuelidate();

function areRulesAreValid() {
  return !hasInvalidRuleConditions.value && !$v.value.$invalid;
}

function hasAnyInvalidRules() {
  return ruleInvalidStates.value.some((invalid: boolean) => invalid);
}
</script>

<style scoped lang="scss">
.follow-up-rules-container {
  margin: auto;
  width: 100%;
  display: flex;
  flex-direction: column;

  .follow-up-rule {
    position: relative;
    display: flex;
    border: 1px rgb(226, 226, 226) solid;
    border-radius: 0.25rem;
    padding: 20px;
    gap: 30px;
    margin-bottom: 20px;
  }
}

.rules-list-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  .rules {
    display: flex;
    flex-direction: column;
    width: 100%;

    .rule {
      margin-bottom: 20px;
    }

    .add-label {
      margin-bottom: 20px;
      margin-left: 1em;

      .add-rule-label {
        margin-left: 0.25em;
      }
    }
  }
}

.rules-message {
  margin-bottom: 15px;
}
</style>
